import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { supabase, type Existencia } from "@/lib/supabase";
import { cantidad } from "@/lib/formato";
import { Input } from "@/components/ui";

/**
 * Buscador de SKU server-side con debounce. Busca por clave, nombre o ubicación dentro
 * del almacén activo. Devuelve la existencia elegida (con producto y físico).
 */
export function filtroBusqueda(q: string): string {
  // PostgREST: el valor va entre comillas dobles; se escapan \ y " y se quitan los comodines.
  const v = q.replace(/[%_]/g, "").replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  const like = `"%${v}%"`;
  return `sku.ilike.${like},nombre.ilike.${like},ubicacion.ilike.${like}`;
}

export function BuscadorSku({ almacenId, onElegir, autoFocus = true, placeholder = "Clave o nombre del producto", mostrarFisico = true }: {
  almacenId: string;
  onElegir: (e: Existencia) => void;
  autoFocus?: boolean;
  placeholder?: string;
  /** false en Contar: el conteo es a ciegas */
  mostrarFisico?: boolean;
}) {
  const [texto, setTexto] = useState("");
  const [q, setQ] = useState("");
  const [abierto, setAbierto] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setQ(texto.trim()), 250);
    return () => window.clearTimeout(t);
  }, [texto]);

  const { data, isFetching, error } = useQuery({
    queryKey: ["existencias", "buscar", almacenId, q],
    enabled: q.length >= 1,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_existencias")
        .select("*")
        .eq("almacen_id", almacenId)
        .eq("activo", true)
        .or(filtroBusqueda(q))
        .order("sku")
        .limit(12);
      if (error) throw error;
      return data;
    },
  });

  function elegir(e: Existencia) {
    onElegir(e);
    setTexto("");
    setQ("");
    setAbierto(false);
    ref.current?.focus();
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-3 h-5 w-5 text-gray-400" aria-hidden />
        <Input
          ref={ref}
          autoFocus={autoFocus}
          value={texto}
          onChange={(e) => { setTexto(e.target.value); setAbierto(true); }}
          onFocus={() => setAbierto(true)}
          onKeyDown={(e) => { if (e.key === "Enter" && data?.length) { e.preventDefault(); elegir(data[0]); } if (e.key === "Escape") setAbierto(false); }}
          placeholder={placeholder}
          className="pl-10"
          aria-label="Buscar producto"
          autoComplete="off"
          enterKeyHint="search"
        />
      </div>
      {abierto && q && (
        <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border border-borde bg-white shadow-lg" role="listbox">
          {isFetching && !data && <li className="px-3 py-2 text-sm text-tinta-suave">Buscando…</li>}
          {error && <li className="px-3 py-2 text-sm text-red-700">No pude buscar. Revisa tu conexión e intenta de nuevo.</li>}
          {data?.length === 0 && <li className="px-3 py-3 text-sm text-tinta-suave">No encuentro “{q}” en este almacén.</li>}
          {data?.map((e) => (
            <li key={e.id}>
              <button type="button" onClick={() => elegir(e)} className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-gray-50">
                <span className="min-w-0">
                  <span className="block font-medium">{e.sku}</span>
                  <span className="block truncate text-sm text-tinta-suave">{e.nombre}</span>
                </span>
                <span className="tabular shrink-0 text-sm text-tinta-suave">{mostrarFisico ? cantidad(e.cantidad_fisica, e.unidad) : e.unidad}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
